import { Link } from 'react-router-dom';
import { CheckIcon, ChevronDownIcon, FileTextIcon, MailIcon, PrinterIcon, RepeatIcon, UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { printPackingSlip } from './packingSlip';
import { ProductImage } from '../../ui/ProductImage';
import { Badge } from '../ui/Badge';
import { useAdmin } from '../AdminContext';
import { btnPrimary, btnSecondary } from '../ui/styles';
import { euro } from '../../core/format/currency';
import { inboxOrderTotal, isPaid, nextStatus, paymentLabel, received, statusLabel, statusTone } from './inboxRules';
import type { InboxOrder } from '../../core/types/adminRequests';
import type { Tracking } from '../../core/types/account';
import { trackingUrl } from '../../core/rules/tracking';
import { formatStreet } from '../../core/rules/contact';
import { isPhysical } from '../../core/rules/stock';
import { ShipForm } from './ShipForm';
type Props = {
  order: InboxOrder;
  open: boolean;
  onToggle: () => void;
  onAdvance: (tracking?: Tracking) => void;
};

/** Eén bestelling: dicht een regel, open de inhoud en de volgende stap. */
export function InboxOrderRow({
  order: o,
  open,
  onToggle,
  onAdvance
}: Props) {
  const {
    index,
    site
  } = useAdmin();
  const sum = inboxOrderTotal(o);
  // Het cadeau komt van de server, vastgelegd bij het plaatsen: nooit opnieuw rekenen met de prijzen van nu.
  const gift = o.gift;
  const paid = isPaid(o);
  // Niet betaald = niet inpakken. De volgende stap verschijnt pas na de betaling.
  const next = paid ? nextStatus[o.status] : undefined;
  const panelId = `order-${o.id}`;
  const addr = o.order.address;
  // Alleen cadeaubonnen of een kuur: er gaat niets op de post (geen pakbon, geen verzendadres).
  const nothingToShip = o.order.lines.every(l => {
    const p = index.productById(l.productId);
    return p !== undefined && !isPhysical(p);
  });
  const slip = async () => {
    const ok = await printPackingSlip(o, index.productById, site.salon, gift);
    if (!ok) toast.error('Pakbon afdrukken lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <li data-id="element-1693" data-mp="3kzapgq16ixso">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="flex w-full flex-wrap items-center gap-x-5 gap-y-1 px-5 py-4 text-left transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-1694" data-mp="3kzkadz7daeri">
        {paid && o.kuurToLink ? <Badge tone="accent" data-id="element-1695" data-mp="378p50ww2ufso">Kuur koppelen</Badge> : <Badge tone={paid ? statusTone[o.status] : 'neutral'} data-id="element-1696" data-mp="328t1eyrakqdn">{paid ? statusLabel[o.status] : paymentLabel[o.payment.status]}</Badge>}
        <span className="min-w-0 flex-1" data-id="element-1697" data-mp="3gy8q8979ilqp">
          <span className="block text-[14px] text-ink" data-id="element-1698" data-mp="3c6qpnm95in69">{o.name}</span>
          <span className="block text-[12px] text-muted" data-id="element-1699" data-mp="3c5fl3k7vtbe3">
            {o.id} · {received(o.receivedAt)} · {o.city}
          </span>
        </span>
        {o.order.skinNote && <>
            <span className="h-2 w-2 shrink-0 rounded-full bg-clay" aria-hidden="true" data-id="element-1700" data-mp="2jhjpyjxynrjf" />
            <span className="sr-only" data-id="element-1701" data-mp="yz8z05684x76">Met notitie</span>
          </>}
        <span className="text-[14px] tabular-nums text-ink" data-id="element-1702" data-mp="13r0osdhk2yw9">{euro(sum, {
          cents: true
        })}</span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-1703" data-mp="p2mczd25w03k" />
      </button>
      {open && <div id={panelId} className="grid gap-6 border-t border-line bg-shell px-5 py-5 md:grid-cols-2" data-id="element-1704" data-mp="1hofk1yuhq0w5">
          <div data-id="element-1705" data-mp="1mnin5dqwtx1h">
            <ul className="space-y-2" data-id="element-1706" data-mp="18pu48bgekzz2">
              {o.order.lines.map(l => {
            const p = index.productById(l.productId);
            return <li key={l.productId} className="flex items-center gap-3 text-[14px]" data-id="element-1707" data-mp="1cpm6k6f7e9vz">
                    {p && <ProductImage src={p.image} className="h-10 w-10 shrink-0 rounded p-0.5" data-id="element-1708" data-mp="3terl5701ebyb" />}
                    <span className="min-w-0 flex-1" data-id="element-1709" data-mp="1dtmhw4b6kgc">
                      <span className="block truncate text-ink" data-id="element-1710" data-mp="hjwxj6tanwf8">
                        {l.qty} × {p?.name ?? l.productId}
                      </span>
                      {l.subscribe && <span className="flex items-center gap-1 text-[12px] text-clay" data-id="element-1711" data-mp="7t28xvskskqm">
                          <RepeatIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-1712" data-mp="2usclhv47e3f" />
                          Automatisch bijvullen
                        </span>}
                    </span>
                  </li>;
          })}
            </ul>
            {gift && <p className="mt-4 text-[13px] text-muted" data-id="element-1713" data-mp="qjbmu2xq60y5">
                Cadeau erbij: <span className="text-ink" data-id="element-1714" data-mp="mihxd54mwe35">{gift}</span>
              </p>}
            {paid && o.kuurToLink && <p className="mt-4 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-1715" data-mp="3bakf7ysp9jox">
                {o.customerId ? 'Deze kuur kon niet automatisch op haar account: ze heeft al een andere lopende kuur. Leg hem vast in het dossier (Kuur › Vooruit betaald vastleggen); daarna verdwijnt deze melding.' : 'Gastbestelling: de kuur staat nog op geen account. Vraag haar een account te maken met dit mailadres, en leg de kuur dan vast in haar dossier.'}
              </p>}
            {!paid && <p className="mt-4 rounded-md bg-sand px-3 py-2 text-[13px] text-ink" data-id="element-1716" data-mp="20k5whnwrjvum">
                {o.payment.status === 'open' ? 'De klant rondt de betaling nog af. Pak hem pas in als hij op Betaald staat.' : 'Niet betaald. Niet inpakken; de klant kan opnieuw bestellen.'}
              </p>}
          </div>
          <div className="space-y-3 text-[13px]" data-id="element-1717" data-mp="2jfw0fgo9b2b5">
            {nothingToShip ? <p className="text-muted" data-id="element-1718" data-mp="k4cpwxnb1lzd">Niets te versturen: cadeaubon per mail, kuur in Mijn omgeving.</p> : <address className="not-italic" data-id="element-1719" data-mp="p3pcrie6et1y">
                <span className="block text-[12px] text-muted" data-id="element-1720" data-mp="p4qycgstixxx">Verzenden naar</span>
                <span className="block text-[14px] leading-snug text-ink" data-id="element-1721" data-mp="1sfxu5o0wqerz">
                  {`${addr.firstName} ${addr.lastName}`.trim() || o.name}
                  <br data-id="element-1722" data-mp="21erw7tx0bxcx" />
                  {formatStreet(addr)}
                  <br data-id="element-1723" data-mp="21c5hnabp8hl7" />
                  {addr.postcode} {addr.city}
                  {addr.country === 'BE' && ' · België'}
                </span>
              </address>}
            {o.order.skinNote && <p className="rounded-md bg-blush/50 px-3 py-2 text-ink" data-id="element-1724" data-mp="26e4iacg210eg">
                <span className="block text-[12px] text-muted" data-id="element-1725" data-mp="26bs1i2m18w2z">Notitie voor jou</span>“{o.order.skinNote}”
              </p>}
            <p className="text-muted" data-id="element-1726" data-mp="2g35wbx31h7ps">
              {o.order.email}
              {o.order.code && <>
                  {' '}
                  · code <span className="text-ink" data-id="element-1727" data-mp="2u0uj5ago2oxh">{o.order.code}</span>
                </>}
              {o.order.refillReminder && ' · wil een seintje als het bijna op is'}
              {!o.customerId && ' · gastbestelling'}
            </p>
            {o.status === 'verzonden' && <p className="text-muted" data-id="element-1728" data-mp="3dmwbqesdv5ew">
                {o.tracking ? <>
                    {o.tracking.carrier} ·{' '}
                    <a href={trackingUrl(o.tracking)} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2" data-id="element-1729" data-mp="33q0225qr0d8r">
                      {o.tracking.code}
                      <span className="sr-only" data-id="element-1730" data-mp="2uqmtdfag7sp3"> (opent in een nieuw tabblad)</span>
                    </a>
                  </> : 'Verzonden zonder track & trace'}
              </p>}
            {next === 'verzonden' && !nothingToShip && <ShipForm orderId={o.id} onShip={onAdvance} data-id="element-1731" data-mp="270shazxye85w" />}
            <div className="grid grid-cols-2 gap-2 pt-1 sm:flex sm:flex-wrap" data-id="element-1732" data-mp="271bxab41mmha">
              {next && next !== 'verzonden' && !nothingToShip && <button type="button" onClick={() => onAdvance()} className={btnPrimary} data-id="element-1733" data-mp="2ri6c16zlex0o">
                  <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-1734" data-mp="2miths16e6no2" />
                  {statusLabel[next]}
                </button>}
              {o.status !== 'verzonden' && paid && !nothingToShip && <button type="button" onClick={() => void slip()} className={btnSecondary} data-id="element-1735" data-mp="2cjkqxh6tpp5x">
                  <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1736" data-mp="28ju0jel7lb1x" />
                  Pakbon
                </button>}
              <Link to={`/be/bestellingen/${o.id}`} state={{
            back: {
              to: '/be/inbox',
              label: 'Inbox'
            }
          }} className={btnSecondary} data-id="element-1737" data-mp="1ysz5pltwd34v">
                <FileTextIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1738" data-mp="1tunxffj930or" />
                Openen
              </Link>
              {o.customerId ? <Link to={`/be/klanten/${o.customerId}`} className={btnSecondary} data-id="element-1739" data-mp="1kt6ezqidemww">
                  <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1740" data-mp="1kvka1zd1vz4g" />
                  Dossier
                </Link> : <a href={`mailto:${o.order.email}`} className={btnSecondary} data-id="element-1741" data-mp="4bpjqrabibo3">
                  <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1742" data-mp="9bbwz0q5brww" />
                  Mail klant
                </a>}
            </div>
          </div>
        </div>}
    </li>;
}